import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { RecruitmentBoardScreen } from '@/features/recruitment-board/board-screen'
import { type BoardSearch, boardSearchSchema } from '@/features/recruitment-board/search'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the recruitment board, at `/recruitment`.
 *
 * The selected opening lives in the URL search params (`opening_id`) so a
 * view is linkable and survives reload — the schema fills nothing, and the
 * screen derives the default opening while the list loads.
 *
 * The grant guard is presentation only. The API enforces
 * `recruitment_candidate.view` on every candidate read, and the module gate
 * refuses the whole `/api/recruitment` prefix for a group whose plan does not
 * include recruitment — a stale grant still 403s server-side. This only keeps
 * a user without the grant out of a screen they cannot use.
 *
 * Per the route-shell rule, this file is a route definition, a guard and a
 * render. The screen lives in `src/features/recruitment-board/`.
 */
export const Route = createFileRoute('/_authenticated/recruitment/')({
  validateSearch: boardSearchSchema,
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'recruitment_candidate.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: RecruitmentBoardRoute,
})

function RecruitmentBoardRoute() {
  const navigate = useNavigate()
  const search = Route.useSearch()

  return (
    <RecruitmentBoardScreen
      search={search}
      onSearchChange={(patch: Partial<BoardSearch>) => {
        // The merged object rather than a reducer: the reducer's parameter is
        // typed as the union of every route's search params, which is not
        // this route's shape. `search` is already this route's, validated.
        void navigate({
          to: '/recruitment',
          search: { ...search, ...patch },
          replace: true,
        })
      }}
      onOpenDashboard={(openingId) => {
        void navigate({ to: '/recruitment/openings/$openingId', params: { openingId } })
      }}
      onOpenCandidate={(candidateId) => {
        void navigate({ to: '/recruitment/candidates/$candidateId', params: { candidateId } })
      }}
    />
  )
}
